function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { CssContainer, Container } from "../../components/container";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import { Button } from "@progress/kendo-react-buttons";
import { PageHeader, Ribbon, Card, Pill, Empty, PlunkDropDown } from "../../components/analytics-kit";
import { Link } from "react-router-dom";
import { hasTenantFeature } from "../../functions/tenant-features";
import { emailSvc, delayLabel, triggerLabel, starterNote, recipientFunnel, num } from "./common";
import { journeySvc, insertNode, emptyGraph } from "../email-journeys/common";
import { errText } from "../reports/common";
import ServiceFactory from "../../service";
import account from "../../data/account";
import { adBillingMode } from "../../functions/tenant-features";

// Content Hub cross-link (CONTENT_CAMPAIGN_INTEGRATION.md §5, Phase C): the
// container this automation's email is a channel leg of, with downstream
// performance.
function contentSvc(api) {
  const Service = ServiceFactory.getType(`/content${api}`, "product"); // /v1 root
  return new Service();
}
function usd(v) {
  return (Number(v) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
}

// "Promote to journey" (EMAIL_JOURNEYS_UX.md §7): an automation IS the
// degenerate journey — trigger → (wait) → email. Build that graph client-side
// from the automation's own configs and create a Draft journey; the automation
// keeps running untouched. Dedupe is per rail, so contacts who already got the
// automation can enter the journey — the confirm copy says so.
export function journeyFromAutomation(a) {
  const sc = a.scheduleConfig || {};
  const tc = a.triggerConfig || {};
  const sd = a.senderConfig || {};
  let graph = emptyGraph();
  let slot = null;
  const delay = {
    delayDays: Number(sc.delayDays) || 0,
    delayHours: Number(sc.delayHours) || 0,
    delayMinutes: Number(sc.delayMinutes) || 0
  };
  if (!sc.sendImmediately && (delay.delayDays || delay.delayHours || delay.delayMinutes)) {
    const r = insertNode(graph, slot, "delay", delay);
    graph = r.graph;
    slot = {
      parentId: r.id,
      edge: "next"
    };
  }
  const r2 = insertNode(graph, slot, "email", {
    name: a.name || "Email",
    emailSubject: a.emailSubject || "",
    emailBody: a.emailBody || "",
    designJson: a.designJson || null,
    emailTemplateId: a.emailTemplateId || null
  });
  graph = r2.graph;
  const known = ["ProductViewed", "CartAbandoned", "WelcomeSeries", "CustomerLoyalty"];
  const triggerType = known.includes(a.triggerType) ? a.triggerType : a.triggerType === "Conversion" ? "Conversion" : "Engagement";
  return {
    name: `${a.name || "Automation"} (journey)`,
    triggerType,
    triggerConfig: {
      eventName: tc.eventName || null,
      eventCategory: tc.eventCategory || null,
      lookbackDays: tc.lookbackDays != null ? tc.lookbackDays : null,
      excludeConverted: tc.excludeConverted !== false
    },
    audienceInclude: a.audienceInclude || [],
    audienceExclude: a.audienceExclude || [],
    senderConfig: {
      fromName: sd.fromName || "",
      fromEmail: sd.fromEmail || ""
    },
    graph,
    goal: {
      kind: null,
      eventName: null
    }
  };
}

/*
 * Automation detail (spec §4): header + Run/Pause toggle, delivery funnel
 * from recipient rows, recipients grid (no server paging yet — the list
 * is capped client-side with an honest count), lifetime stats.
 * "Test run" is deliberately ABSENT: today's evaluate endpoint runs every
 * automation globally, so the button waits until the API honors the id.
 */

const RECIPIENTS_SHOWN = 200;
class EmailAutomationDetailViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "loadAudiences", async () => {
      try {
        const S = ServiceFactory.getType("/audience", "audience");
        const r = await new S().getAsync();
        const items = Array.isArray(r) ? r : r && (r.items || r.value) || [];
        this.setState({
          audiences: items.filter(a => a && a.name)
        });
      } catch (e) {/* no audiences */}
    });
    // One-off send (Content Hub → email): queue an audience into this automation; the worker applies consent + dedup.
    _defineProperty(this, "sendToAudience", async () => {
      const {
        automation: a,
        sendAudience,
        sendAt
      } = this.state;
      if (!a || !sendAudience) return;
      this.setState({
        sending: true,
        sendNote: null
      });
      try {
        const aud = this.state.audiences.find(x => String(x.id) === String(sendAudience));
        await emailSvc(`/automations/${a.id}/send`).postAsync({
          audienceId: String(sendAudience),
          sendAt: sendAt ? new Date(sendAt).toISOString() : null
        });
        this.setState({
          sending: false,
          sendNote: `Queued ${aud ? `"${aud.name}"` : "the audience"}${sendAt ? ` for ${new Date(sendAt).toLocaleString()}` : ""}: contacts who opted out or already received this are skipped; sends go out within 5 minutes and appear under Recipients.`
        });
        setTimeout(() => this.componentDidMount(), 4000);
      } catch (e) {
        this.setState({
          sending: false,
          sendNote: e && e.message && e.message.length < 200 ? e.message : "Could not queue the send."
        });
      }
    });
    _defineProperty(this, "toggle", async () => {
      const a = this.state.automation;
      if (!a || this.state.busy) return;
      const next = a.status === "Running" ? "Paused" : "Running";
      this.setState({
        busy: true,
        writeError: null
      });
      try {
        await emailSvc(`/automations/${a.id}/status?status=${next}`).putAsync({});
        // §288.3: never flip optimistically — DS's first activation pass
        // "showed success" while every row stayed Draft. Re-read the row;
        // the chip renders what the server persisted, or we say so.
        const fresh = await emailSvc(`/automations/${a.id}?range=DAY`).getAsync();
        if (!this._alive) return;
        const persisted = fresh && fresh.status || a.status;
        this.setState({
          automation: fresh || a,
          busy: false,
          writeError: persisted === next ? null : `The status change didn't stick: the automation is still ${String(persisted).toLowerCase()}. Try again.`
        });
      } catch (e) {
        // 422 {code: missingAddress, error}: the brand kit needs a mailing address before anything sends.
        if (this._alive) this.setState({
          busy: false,
          writeError: errText(e, "The status change failed: try again.")
        });
      }
    });
    _defineProperty(this, "promote", async () => {
      const a = this.state.automation;
      if (!a || this.state.promoting) return;
      this.setState({
        promoting: true,
        writeError: null
      });
      try {
        // Server-side since 2026-09-12 (POST automations/{id}/promote) so the App
        // and Ask Plunk share one mapping; journeyFromAutomation() stays as the
        // documented shape / fallback.
        let created;
        try {
          created = await emailSvc(`/automations/${a.id}/promote`).postAsync({});
        } catch (e) {
          created = await journeySvc().postAsync(journeyFromAutomation(a));
        }
        if (!created || !created.id) throw new Error("Create returned no id");
        if (this._alive) this.props.history.push(`/Email/journeys/edit/${created.id}`);
      } catch (e) {
        if (this._alive) this.setState({
          promoting: false,
          confirmPromote: false,
          writeError: errText(e, "Couldn't create the journey: try again.")
        });
      }
    });
    this.state = {
      audiences: [],
      sendAudience: "",
      sendAt: "",
      sending: false,
      sendNote: null,
      automation: null,
      recipients: [],
      error: false,
      busy: false,
      writeError: null,
      shown: RECIPIENTS_SHOWN,
      confirmPromote: false,
      promoting: false,
      content: null
    };
    this._alive = true;
  }
  id() {
    return this.props.params.id;
  }
  async componentDidMount() {
    this.loadAudiences();
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    try {
      const [automation, recipients] = await Promise.all([emailSvc(`/automations/${this.id()}?range=DAY`).getAsync(), emailSvc(`/automations/${this.id()}/recipients`).getAsync().catch(() => [])]);
      if (this._alive) this.setState({
        automation,
        recipients: recipients || []
      });
      // Content Hub link (§3.1/§5): the linked container + its funnel.
      if (automation && automation.containerId) {
        const [detail, funnel] = await Promise.all([contentSvc(`/containers/detail/${automation.containerId}`).getAsync().catch(() => null), contentSvc(`/containers/detail/${automation.containerId}/funnel/this_quarter`).getAsync().catch(() => null)]);
        if (this._alive && detail && detail.container) {
          this.setState({
            content: {
              id: automation.containerId,
              title: detail.container.title,
              funnel: funnel || {}
            }
          });
        }
      }
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }
  componentWillUnmount() {
    this._alive = false;
  }
  render() {
    if (!hasTenantFeature("emailAutomation")) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Email Automation isn't configured for this workspace."));
    }
    const {
      automation: a,
      recipients,
      error,
      busy,
      writeError,
      shown
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, {
      message: "This automation could not be loaded."
    });
    if (!a) return /*#__PURE__*/React.createElement(Loader, null);
    const m = a.metrics || {};
    const f = recipientFunnel(recipients);
    const note = starterNote(a);
    const sender = a.senderConfig || {};
    const canRun = !(note && note.comingSoon);
    const tone = {
      running: "success",
      paused: "warning",
      draft: "neutral",
      archived: "neutral"
    }[String(a.status || "").toLowerCase()] || "neutral";
    const sent = Math.max(f.sent, Number(m.sent) || 0);
    const opens = Number(m.uniqueOpens) || 0;
    const clicks = Number(m.uniqueClicks) || 0;
    const pct = n => sent > 0 ? `${Math.round(n / sent * 100)}% of sent` : "arrives with delivery tracking";
    const actions = /*#__PURE__*/React.createElement("span", {
      className: "wa-actions"
    }, this.state.confirmPromote ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
      disabled: this.state.promoting,
      onClick: () => this.setState({
        confirmPromote: false
      })
    }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: this.state.promoting,
      onClick: this.promote
    }, this.state.promoting ? "Creating…" : "Yes, create the journey")) : /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        confirmPromote: true
      }),
      title: "Create a draft journey from this automation's trigger, wait and email: then add more steps"
    }, "Promote to journey"), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.props.history.push(`/Email/automations/edit/${a.id}`)
    }, "Edit"), a.status !== "Draft" ? /*#__PURE__*/React.createElement(Button, {
      themeColor: a.status === "Running" ? "base" : "primary",
      disabled: busy,
      onClick: this.toggle
    }, busy ? "Saving…" : a.status === "Running" ? "Pause" : "Resume") : /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: busy || !canRun,
      onClick: this.toggle,
      title: canRun ? "Start sending" : note && note.note || ""
    }, busy ? "Saving…" : "Activate"));
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        to: "/Email/journeys"
      }, "Journeys"), " \xB7 ", /*#__PURE__*/React.createElement(Link, {
        to: "/Email/automations"
      }, "Automations"), " \xB7 Automation"),
      title: /*#__PURE__*/React.createElement(React.Fragment, null, a.name, " ", /*#__PURE__*/React.createElement(Pill, {
        tone: tone
      }, String(a.status || "").toLowerCase())),
      subtitle: `${triggerLabel(a)} · ${delayLabel(a.scheduleConfig)} · from ${sender.fromName || "—"}${sender.fromEmail ? ` <${sender.fromEmail}>` : ""} · each contact receives it once ever`,
      actions: actions
    }), note ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice warning"
    }, note.note) : null, writeError ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger"
    }, writeError) : null, this.state.confirmPromote ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, /*#__PURE__*/React.createElement("b", null, "Promote to journey"), " creates a ", /*#__PURE__*/React.createElement("i", null, "draft"), " journey with the same trigger, audience and sender, and this automation's ", delayLabel(a.scheduleConfig) === "immediately" ? "email as its first step" : "wait and email as its first steps", ": then you add more. This automation keeps running unchanged until you pause it. Contacts who already received it can still enter the journey (each rail remembers its own sends).") : null, /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      tiles: [{
        l: "Triggered",
        v: num(f.entered),
        s: f.waiting > 0 ? `${num(f.waiting)} waiting on delay` : "contacts that entered"
      }, {
        l: "Sent",
        v: num(sent),
        s: f.bounced > 0 ? `${num(f.bounced)} bounced or unsubscribed` : f.entered ? `${Math.round(sent / Math.max(1, f.entered) * 100)}% of triggered` : null
      }, {
        l: "Opened",
        v: sent > 0 && opens > 0 ? num(opens) : "—",
        s: pct(opens)
      }, {
        l: "Clicked",
        v: sent > 0 && clicks > 0 ? num(clicks) : "—",
        s: pct(clicks)
      }]
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Email",
      hint: a.emailSubject ? "subject · sender · preview" : "not written yet",
      span: this.state.content ? 6 : 12
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        fontSize: 13
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        margin: "0 0 4px"
      }
    }, /*#__PURE__*/React.createElement("b", null, "Subject:"), " ", a.emailSubject || "—"), /*#__PURE__*/React.createElement("p", {
      style: {
        margin: "0 0 4px"
      }
    }, /*#__PURE__*/React.createElement("b", null, "From:"), " ", sender.fromName || "—", " ", sender.fromEmail ? `<${sender.fromEmail}>` : ""), (String(a.triggerType || "").toLowerCase() === "manual" || String(a.type || "").toLowerCase() === "campaign") && /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10,
        paddingTop: 10,
        borderTop: "1px dashed var(--plk-border)"
      }
    }, /*#__PURE__*/React.createElement("b", null, "Send to an audience"), " ", /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "\xB7 one-off send; each contact receives it once, consent and the daily cap apply"), /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar",
      style: {
        marginTop: 6
      }
    }, /*#__PURE__*/React.createElement(PlunkDropDown, {
      options: [["", "Choose an audience…"]].concat(this.state.audiences.map(x => [String(x.id), `${x.name}${x.total != null ? ` · ${num(x.total)}` : ""}`])),
      value: this.state.sendAudience,
      onChange: v => this.setState({
        sendAudience: v
      }),
      width: "260px"
    }), /*#__PURE__*/React.createElement("input", {
      className: "cust-search wa-select",
      style: {
        width: 200
      },
      type: "datetime-local",
      value: this.state.sendAt,
      onChange: e => this.setState({
        sendAt: e.target.value
      })
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !this.state.sendAudience || this.state.sending || !a.emailSubject || !a.emailBody,
      onClick: this.sendToAudience
    }, this.state.sending ? "Queuing…" : this.state.sendAt ? "Schedule send" : "Send now")), this.state.sendNote && /*#__PURE__*/React.createElement("p", {
      className: "dim",
      style: {
        margin: "6px 0 0"
      }
    }, this.state.sendNote)), a.emailBody ? /*#__PURE__*/React.createElement("details", {
      style: {
        marginTop: 8
      }
    }, /*#__PURE__*/React.createElement("summary", {
      style: {
        cursor: "pointer",
        color: "var(--plk-accent)"
      }
    }, "Preview email body"), /*#__PURE__*/React.createElement("div", {
      style: {
        border: "1px solid var(--plk-border)",
        borderRadius: 6,
        marginTop: 8,
        padding: 10,
        maxHeight: 420,
        overflow: "auto",
        background: "var(--plk-surface)"
      },
      dangerouslySetInnerHTML: {
        __html: a.emailBody
      }
    })) : /*#__PURE__*/React.createElement(Empty, null, "No email content yet: add it in Edit before activating."))), this.state.content ? (() => {
      const ct = this.state.content;
      const cf = ct.funnel || {};
      const moneyVisible = adBillingMode() === "selfmanaged";
      return /*#__PURE__*/React.createElement(Card, {
        title: "Content used here",
        hint: "this automation's clicks feed this content's funnel",
        span: 6,
        more: "Content Hub \u2192",
        onMore: () => this.props.history.push("/Content")
      }, /*#__PURE__*/React.createElement("div", {
        className: "wa-minikpis"
      }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
        className: "v"
      }, num(cf.sessions || 0)), /*#__PURE__*/React.createElement("div", {
        className: "l"
      }, "Sessions")), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
        className: "v"
      }, num(cf.conversions || 0)), /*#__PURE__*/React.createElement("div", {
        className: "l"
      }, "Conversions")), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
        className: "v"
      }, moneyVisible && Number(cf.salesValue) > 0 ? usd(cf.salesValue) : "—"), /*#__PURE__*/React.createElement("div", {
        className: "l"
      }, "Sales")), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
        className: "v",
        style: {
          fontSize: 15
        }
      }, ct.title || `Content #${ct.id}`), /*#__PURE__*/React.createElement("div", {
        className: "l"
      }, "Piece"))));
    })() : null, /*#__PURE__*/React.createElement(Card, {
      title: "Recipients",
      hint: `${num(recipients.length)} contacts`
    }, !recipients.length ? /*#__PURE__*/React.createElement(Empty, null, "No contacts have entered this automation yet.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Email"), /*#__PURE__*/React.createElement("th", null, "Status"), /*#__PURE__*/React.createElement("th", null, "Scheduled"), /*#__PURE__*/React.createElement("th", null, "Sent"))), /*#__PURE__*/React.createElement("tbody", null, recipients.slice(0, shown).map(r => /*#__PURE__*/React.createElement("tr", {
      key: r.id
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, r.email)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Pill, {
      tone: r.status === "Pending" ? "warning" : "success"
    }, String(r.status || "").toLowerCase())), /*#__PURE__*/React.createElement("td", {
      className: "dim"
    }, r.scheduledDate ? new Date(r.scheduledDate).toLocaleString() : "—"), /*#__PURE__*/React.createElement("td", {
      className: "dim"
    }, r.sentDate ? new Date(r.sentDate).toLocaleString() : "—")))))), recipients.length > shown ? /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar",
      style: {
        marginTop: 10
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "wa-chip",
      onClick: () => this.setState({
        shown: shown + RECIPIENTS_SHOWN
      })
    }, "Show more \xB7 ", num(recipients.length - shown), " more")) : null))));
  }
}
export default withRouter(EmailAutomationDetailViewComponent);